import {CRecordClass} from 'app/modules/discovery/core/newmodels/_base'
import {queryClient} from 'app/react-query'
import {
  JsonData,
  JsonObject,
  JsonObjectWithId,
  cloneJson,
  jsonDataIsArrayWithIds,
  jsonDataIsObj,
  jsonDataIsObjWithId,
} from 'utilities/types'
import {ParentContext} from './ApiParentContext'
import {ApiReversibleUpdate, ApiUpdateBatch} from './ApiReversibleUpdate'

/*
We use ApiReferences to keep track of where a given object is referenced (that is, in which
cache locations). If the object is updated or deleted, we will have to modify each of these
items accordingly.

As an example, suppose we have loaded a project which has a saved search with a concept with
a note. If that note is changed, we need to modify the project object to reflect that change.
The ApiReference would look like:

{
    id: <project id>,
    recordClass: CProject,
    chain: ['saved_searches', <saved search id>, 'concepts', <concept id>, 'notes']
}

When we call modify() on this ApiReference, we pass it a function which will take a list of
entities and return an updated version of it. This function will be given the current list of
notes on that concept in that search.

*/
class ApiReference {
  ownerId
  ownerClass
  targetId
  targetClass
  chain

  constructor(
    ownerId: string,
    ownerClass: CRecordClass,
    targetId: string,
    targetClass: CRecordClass,
    chain: string[]
  ) {
    this.ownerId = ownerClass.isRoot ? 'root' : ownerId
    this.ownerClass = ownerClass
    this.targetId = targetId
    this.targetClass = targetClass
    this.chain = chain
  }

  get refId() {
    return `${this.ownerId}|${this.ownerClass.name}|${this.chain.join(':')}:${this.targetId}`
  }

  get ownerItemKey() {
    return ['records', this.ownerId]
  }

  get ownerListKey() {
    return ['records', this.ownerId, this.chain[0]]
  }

  get ownerItem() {
    return queryClient.getQueryData(this.ownerItemKey) as JsonObjectWithId | undefined
  }

  get ownerList() {
    return queryClient.getQueryData(this.ownerListKey) as JsonObjectWithId[] | undefined
  }

  modify(func: (arr: JsonObjectWithId[]) => JsonObjectWithId[]) {
    const itemUpdate = this._modify(func, false)
    const listUpdate = this._modify(func, true)
    return [itemUpdate, listUpdate].filter((x) => x) as ApiReversibleUpdate[]
  }

  // Returns the list which contains the cached target, along with its surrounding context.
  targetList(args?: {data?: JsonData; fromList?: boolean}) {
    const fromList = !!args?.fromList
    let data: JsonObjectWithId | undefined
    let subchain: string[]
    const finalprop = this.chain[this.chain.length - 1]

    if (fromList) {
      if (args?.data !== undefined && !jsonDataIsArrayWithIds(args.data)) {
        throw new Error(
          `Invalid data: Expected array of objects with ids, got: ${JSON.stringify(args.data)}`
        )
      }
      data = {id: this.ownerId, [this.chain[0]]: args?.data ?? this.ownerList}
      subchain = this.chain.slice(1, this.chain.length - 1)
    } else {
      if (args?.data !== undefined && !jsonDataIsObjWithId(args.data)) {
        throw new Error(`Invalid data: Expected object with id, got: ${JSON.stringify(args.data)}`)
      }
      data = args?.data ?? this.ownerItem
      subchain = this.chain.slice(0, this.chain.length - 1)
    }
    if (data === undefined) {
      return undefined
    }

    let wrapper: JsonData = data
    for (const subprop of subchain) {
      if (jsonDataIsArrayWithIds(wrapper)) {
        wrapper = wrapper.filter((x) => x.id === subprop)
        if (wrapper.length === 0) {
          return undefined
        }
        wrapper = wrapper[0]
      } else if (jsonDataIsObj(wrapper)) {
        wrapper = wrapper[subprop]
      } else {
        return undefined
      }
    }
    if (!jsonDataIsObj(wrapper)) {
      return undefined
    }
    const list = wrapper[finalprop]
    if (!jsonDataIsArrayWithIds(list)) {
      return undefined
    }
    return {list, listParent: wrapper, prop: finalprop}
  }

  // Returns the actual cached object, along with the list which contains it.
  targetItem(args?: {data?: JsonData; fromList?: boolean}) {
    const listData = this.targetList(args)
    if (!listData) {
      return undefined
    }
    for (const item of listData.list) {
      if (item.id === this.targetId) {
        return {item, list: listData.list}
      }
    }
    return undefined
  }

  _modify(func: (arr: JsonObjectWithId[]) => JsonObjectWithId[], listCache: boolean) {
    // Important to clone the data before making changes. We are going to
    // return a reversible update object, so that the change can be reverted
    // if the API call fails.
    const original = listCache ? this.ownerList : this.ownerItem
    const updated = cloneJson(original)

    const targetList = this.targetList({data: updated, fromList: listCache})
    if (!targetList) {
      return undefined
    }
    const {list, listParent, prop} = targetList
    listParent[prop] = func(list)
    return new ApiReversibleUpdate(
      listCache ? this.ownerListKey : this.ownerItemKey,
      this.ownerClass,
      () => updated
    )
  }
}

// When an object is loaded, construct references for all of the ids referenced in that object.
function constructDescendantRefs(data: JsonObjectWithId, recordClass: CRecordClass) {
  return _constructDescendantRefs(data.id, recordClass, data, recordClass, [])
}

function _constructDescendantRefs(
  ownerId: string,
  ownerClass: CRecordClass,
  descendantData: JsonObjectWithId,
  descendantClass: CRecordClass,
  chain: string[]
) {
  const output: ApiReference[] = []
  if (chain.length) {
    output.push(new ApiReference(ownerId, ownerClass, descendantData.id, descendantClass, chain))
  }
  for (const path of descendantClass.childPaths) {
    const list = descendantData[path.prop]
    if (jsonDataIsArrayWithIds(list)) {
      for (const item of list) {
        let newChain: string[]
        if (!chain.length) {
          newChain = [path.prop]
        } else {
          newChain = [...chain, descendantData.id, path.prop]
        }
        output.push(
          ..._constructDescendantRefs(ownerId, ownerClass, item, path.childClass, newChain)
        )
      }
    }
  }
  return output
}

// When a new object is created, construct references for all of the ancestors of that object.
function constructAncestorRefs(
  data: JsonObjectWithId,
  recordClass: CRecordClass,
  parentContext?: ParentContext
) {
  return _constructAncestorRefs(data.id, recordClass, data, parentContext, [])
}

function _constructAncestorRefs(
  targetId: string,
  recordClass: CRecordClass,
  data: JsonObjectWithId,
  parentContext: ParentContext | undefined,
  chain: string[]
) {
  const output: ApiReference[] = []
  const contexts = recordClass.parentContexts(data)
  if (parentContext && !contexts.map((x) => x.contextId).includes(parentContext.contextId)) {
    contexts.push(parentContext)
  }
  for (const context of contexts) {
    const newChain: string[] = [context.prop]
    if (chain.length) {
      newChain.push(data.id, ...chain)
    }

    output.push(
      new ApiReference(
        context.parent.id,
        context.parent.recordClass,
        targetId,
        context.child.recordClass,
        newChain
      )
    )
    const parentData = queryClient.getQueryData(['records', context.parent.id]) as JsonData
    if (jsonDataIsObjWithId(parentData)) {
      const parentRelations = new ApiRelations(context.parent.recordClass)
      output.push(
        ..._constructAncestorRefs(
          targetId,
          context.parent.recordClass,
          parentData,
          undefined,
          newChain
        )
      )
    }
  }
  return output
}

export class ApiRelations {
  static _references: {[targetId: string]: {[refId: string]: ApiReference}} = {}

  recordClass

  constructor(recordClass: CRecordClass) {
    this.recordClass = recordClass
  }

  _insertRef(ref: ApiReference) {
    if (!(ref.targetId in ApiRelations._references)) {
      ApiRelations._references[ref.targetId] = {}
    }
    ApiRelations._references[ref.targetId][ref.refId] = ref
  }

  // Given a newly retrieved record from the database, generate and store its refs.
  load(data: JsonObjectWithId) {
    const refs = constructDescendantRefs(data, this.recordClass)
    for (const ref of refs) {
      this._insertRef(ref)
    }
  }

  // Given a newly retrieved list from the database, generate and store its refs.
  loadList(id: string, prop: string, list: JsonObjectWithId[]) {
    const refs = constructDescendantRefs({id, [prop]: list}, this.recordClass)
    for (const ref of refs) {
      this._insertRef(ref)
    }
  }

  retrieve(id: string) {
    const primaryCache = queryClient.getQueryData(['records', id]) as JsonObjectWithId
    if (primaryCache !== undefined) {
      return primaryCache
    }
    const refs = ApiRelations._references[id]
    if (refs) {
      for (const refId in refs) {
        const itemCache = refs[refId].targetItem()
        if (itemCache) {
          return itemCache.item
        }
      }
      for (const refId in refs) {
        const listCache = refs[refId].targetItem({fromList: true})
        if (listCache) {
          return listCache.item
        }
      }
    }
    return undefined
  }

  // Create a new record and insert it everywhere it needs to go.
  create(data: JsonObjectWithId, opts?: {parentContext?: ParentContext; autocommit?: boolean}) {
    this.load(data)

    const {parentContext, autocommit} = opts ?? {}
    const refs = constructAncestorRefs(data, this.recordClass, parentContext)
    const updates = [new ApiReversibleUpdate(['records', data.id], this.recordClass, () => data)]
    for (const ref of refs) {
      this._insertRef(ref)
      const update = ref.modify((arr) => {
        if (!arr.map((x) => x.id).includes(data.id)) {
          return [...arr, data]
        }
        return arr
      })
      updates.push(...update)
    }
    const keys = updates.map((x) => x.queryKey)
    return new ApiUpdateBatch(updates, {
      autocommit,
      onCommit: () => {
        for (const key of keys) {
          queryClient.invalidateQueries(key)
        }
      },
    })
  }

  // Make a change to an existing record, and update all refs to reflect it.
  update(id: string, data: JsonObject, opts?: {autocommit?: boolean}) {
    const {autocommit} = opts ?? {}
    const updates = [
      new ApiReversibleUpdate(['records', id], this.recordClass, (original) =>
        this.recordClass.merge(original as JsonObjectWithId | undefined, data)
      ),
    ]
    if (id in ApiRelations._references) {
      for (const refId in ApiRelations._references[id]) {
        const ref = ApiRelations._references[id][refId]
        const update = ref.modify((arr) =>
          arr.map((item) => {
            if (item.id === id) {
              return this.recordClass.merge(item, data)
            }
            return item
          })
        )
        updates.push(...update)
      }
    }
    const keys = updates.map((x) => x.queryKey)
    return new ApiUpdateBatch(updates, {
      autocommit,
      onCommit: () => {
        for (const key of keys) {
          queryClient.invalidateQueries(key)
        }
      },
    })
  }

  // Delete an existing record, and remove it from all refs.
  remove(id: string, opts?: {autocommit?: boolean}) {
    const {autocommit} = opts ?? {}
    const updates = [
      new ApiReversibleUpdate(['records', id], this.recordClass, (original) => undefined),
    ]
    if (id in ApiRelations._references) {
      for (const refId in ApiRelations._references[id]) {
        const ref = ApiRelations._references[id][refId]
        const update = ref.modify((arr) => arr.filter((item) => item.id !== id))
        updates.push(...update)
      }
    }
    const keys = updates.map((x) => x.queryKey)
    return new ApiUpdateBatch(updates, {
      autocommit,
      onCommit: () => {
        for (const key of keys) {
          if (key[1] !== id) {
            queryClient.invalidateQueries(key)
          }
        }
        delete ApiRelations._references[id]
      },
    })
  }
}
